import {
  activityToAnimation,
  bodyStage,
  dayKey,
  formNow,
  visibleBodyStage,
  levelForXp,
  isWorkTime,
  modeActive,
  pickIdleBehavior,
  pickOuting,
  type CompanionActivity,
  type GameEvent,
  type GameState,
  type IdleBehavior,
} from "@squire/core";
import {
  ANCHORS,
  ANIMATIONS,
  BEHAVIOR_ANIMATION,
  PARK_PATH,
  PLACE_SPOTS,
  SEQUENCES,
  WALK_Y,
  frameAt,
  itemCenterX,
  type AwayPlace,
  type Place,
  type Pose,
  type Step,
} from "@squire/sprites";

export type ConversationMode = "talk" | "listen" | "think" | null;

export interface DirectorFrame {
  anim: string;
  frameIndex: number;
  pose: Pose;
  x: number;
  y: number;
  flip: boolean;
  hidden: boolean;
  doorOpen: boolean;
  flash: boolean;
  stage: number;
  readingGlasses: boolean;
  /** Which scene to draw: the flat or a place he went to. */
  place: Place;
  /** The friend met there (café, bar, park walk). */
  friend: FriendFrame | null;
}

export interface FriendFrame {
  seed: number;
  anim: string;
  frameIndex: number;
  pose: Pose;
  x: number;
  y: number;
  flip: boolean;
}

/**
 * Time away from the flat. social = meeting someone (café, bar, park) or a short office visit;
 * outing = his own walk or shopping (ends on interaction); work = his weekday job in the office.
 * place null = just out (no scene for it, the flat stays empty).
 */
interface Visit {
  place: AwayPlace | null;
  since: number;
  until: number;
  friend: boolean;
  seed: number;
  kind: "social" | "outing" | "work";
}

/** Social activities happen away from home: activity animation → place. */
const VISIT_PLACE: Record<string, AwayPlace> = {
  visit_cafe: "cafe",
  visit_bar: "bar",
  visit_park: "park",
};
/** How long the meeting is shown (his re-enactment of what the player told). */
const VISIT_MS = 25_000;
const OFFICE_VISIT_MS = 20_000;

export interface DirectorContext {
  state: GameState;
  now: number;
  night: boolean;
  /** Companion is on another device. */
  away: boolean;
  /** Time since the last touch, talk or camera motion; outings happen only when it is quiet. */
  quietForMs?: number;
}

/** Animation for something the companion said he does ("też się kładę"). */
export function companionAnimation(activity: CompanionActivity, owned: ReadonlySet<string>): string {
  switch (activity) {
    case "train":
      return owned.has("dumbbells") ? "dumbbells" : "pushups";
    case "clean":
      return owned.has("vacuum") ? "vacuum" : "sweep";
    case "work":
      return owned.has("laptop") || owned.has("desk") ? "laptop" : "write";
    case "play":
      return owned.has("console") ? "play_console" : owned.has("guitar") ? "guitar" : "sit";
    case "walk_out":
    case "shopping_out":
      return "walk";
    default:
      return activity;
  }
}

/** An outing does not end on the companion's own announcement, only on later interaction. */
const OUTING_GRACE_MS = 15_000;

const MAX_QUEUE = 4;
const WALK_MIN_X = 34;
const WALK_MAX_X = 156;

/**
 * Avatar state machine. Decides what the avatar does each tick:
 * sequence (door, carrying) > conversation > activity > state (sleep, sick, muted) > idle behaviours.
 * Pure logic with injected time and random, so it is testable without a canvas.
 */
export class Director {
  x = ANCHORS.center!.x;
  y = WALK_Y;
  flip = false;
  hidden = false;
  doorOpen = false;
  conversation: ConversationMode = null;

  private anim = "idle";
  private animStart = 0;
  private lastUpdate = 0;
  private target: { x: number; y: number; anim: string } | null = null;
  private seq: Step[] = [];
  private seqItem: string | null = null;
  private stepUntil = 0;
  private stepStarted = false;
  private queue: { name: string; item: string | null }[] = [];
  private activity: { anim: string; until: number } | null = null;
  private idle: { behavior: IdleBehavior; anim: string; until: number } | null = null;
  private prevBehavior: IdleBehavior | null = null;
  private flashUntil = 0;
  private wasAway = false;
  /** Out of the flat (see Visit). */
  private visit: Visit | null = null;
  /** Debug: keep his own schedule (office, outings) away until then. */
  private holdUntil = 0;
  /** Scene currently shown. */
  place: Place = "home";
  private outingsDay = "";
  private outingsToday = 0;
  /** Items being carried in; the room hides them until placed. Bounded by MAX_QUEUE. */
  readonly pendingItems = new Set<string>();

  constructor(private readonly random: () => number = Math.random) {}

  /** React to game events from the server. */
  handleEvents(events: readonly GameEvent[], state: GameState, now: number): void {
    const owned = new Set(state.inventory.map((i) => i.id));
    for (const e of events) {
      switch (e.type) {
        case "xp": {
          const anim = activityToAnimation(e.animation, owned);
          const place = VISIT_PLACE[anim];
          if (place) this.startVisit(place, now, VISIT_MS, "social", true);
          else if (e.area === "work" && this.visit?.place !== "office")
            this.startVisit("office", now, OFFICE_VISIT_MS, "social", false);
          else if (anim === "trash_out" || anim === "shop_return") this.enqueue(anim, null);
          else this.playActivity(anim, now, 10_000 + Math.floor(this.random() * 10_000));
          break;
        }
        case "body_stage":
          this.flashUntil = now + 1200;
          this.playActivity("stage_up", now, 5000);
          break;
        case "level_up":
        case "celebrate":
          if (!this.activity || this.activity.anim !== "stage_up") this.playActivity("celebrate", now, 3500);
          break;
        case "companion": {
          const ms = Math.max(0, e.until - now);
          if (e.activity === "walk_out") this.startVisit("park", now, ms, "outing", false);
          else if (e.activity === "shopping_out") this.startVisit(null, now, ms, "outing", false);
          else
            this.playActivity(
              companionAnimation(e.activity, owned),
              now,
              e.activity === "celebrate" ? 3500 : ms,
            );
          break;
        }
        case "item":
          if (this.queue.length < MAX_QUEUE) {
            this.pendingItems.add(e.itemId);
            this.enqueue("new_item", e.itemId);
          }
          break;
        default:
          break;
      }
    }
  }

  playActivity(anim: string, now: number, ms: number): void {
    if (!ANIMATIONS[anim]) anim = "nod";
    this.activity = { anim, until: now + ms };
    this.idle = null;
  }

  enqueue(name: string, item: string | null): void {
    if (!SEQUENCES[name] || this.queue.length >= MAX_QUEUE) return;
    this.queue.push({ name, item });
  }

  /** Companion moved away / came back (location events). */
  leave(): void {
    this.queue = [{ name: "leave", item: null }];
    this.seq = [];
    this.activity = null;
  }

  arrive(): void {
    this.hidden = true;
    this.x = ANCHORS.door!.x;
    this.y = ANCHORS.door_out!.y;
    this.queue = [{ name: "arrive", item: null }];
    this.seq = [];
  }

  /**
   * Go somewhere for a while: out through the door, then the scene switches to the place.
   * A new visit replaces the current one (a coffee with a friend interrupts the office).
   */
  startVisit(place: AwayPlace | null, now: number, ms: number, kind: Visit["kind"], friend: boolean): void {
    if (this.hidden && !this.visit) return;
    const leaving = !this.visit;
    this.visit = { place, since: now, until: now + ms, friend, seed: Math.floor(this.random() * 1e6), kind };
    this.activity = null;
    this.idle = null;
    if (leaving) this.enqueue("leave", null);
  }

  /** Touch, talk or motion: come back home from his own outing (not right after announcing it). */
  comeBack(now: number): void {
    if (this.visit?.kind === "outing" && now - this.visit.since > OUTING_GRACE_MS) this.endVisit();
  }

  /** Debug: come home now and do this animation, without the office pulling him away. */
  debugAnim(anim: string, now: number, ms: number): void {
    this.holdUntil = now + ms;
    if (this.visit) this.endVisit();
    this.playActivity(anim, now, ms);
  }

  /** Debug: go somewhere (or home) right now. */
  debugGo(place: AwayPlace | "out" | "home", now: number, ms: number, friend: boolean): void {
    this.holdUntil = now + ms;
    if (place === "home") {
      if (this.visit) this.endVisit();
      return;
    }
    this.startVisit(place === "out" ? null : place, now, ms, "social", friend);
  }

  private endVisit(): void {
    this.visit = null;
    this.place = "home";
    this.flip = false;
    this.arrive();
  }

  isBusy(): boolean {
    return this.seq.length > 0 || this.queue.length > 0;
  }

  update(ctx: DirectorContext): void {
    const { now } = ctx;
    const dt = Math.min(250, Math.max(0, now - (this.lastUpdate || now)));
    this.lastUpdate = now;

    if (ctx.away && !this.wasAway && !this.hidden && this.seq.length === 0) this.leave();
    if (!ctx.away && this.wasAway) this.arrive();
    this.wasAway = ctx.away;

    if (this.target) {
      this.stepTowards(dt);
      return;
    }

    if (this.seq.length || this.queue.length) {
      this.runSequence(ctx);
      return;
    }

    if (ctx.away) {
      this.visit = null;
      this.place = "home";
      this.hidden = true;
      return;
    }
    // His weekday job: off to the office (a meeting or a walk may interrupt, then he goes back).
    const working = isWorkTime(ctx.state, now);
    if (working && !this.visit && !ctx.night && now >= this.holdUntil)
      this.startVisit("office", now, 12 * 3600_000, "work", false);
    if (this.visit) {
      const v = this.visit;
      const over =
        now >= v.until ||
        (v.kind === "work" && !working) ||
        (v.kind === "outing" && this.conversation !== null && now - v.since > OUTING_GRACE_MS);
      if (over) this.endVisit();
      else this.atPlace(v, now);
      return;
    }
    this.hidden = false;

    if (this.conversation) {
      this.setAnim(this.conversation, now);
      this.flip = false;
      return;
    }

    if (this.activity) {
      if (now < this.activity.until) {
        if (this.goToAnchorOf(this.activity.anim)) return;
        this.setAnim(this.activity.anim, now);
        return;
      }
      this.activity = null;
    }

    const stateAnim = this.stateAnimation(ctx);
    if (stateAnim) {
      if (this.goToAnchorOf(stateAnim)) return;
      this.setAnim(stateAnim, now);
      this.idle = null;
      return;
    }

    if (!this.idle || now >= this.idle.until) {
      const day = dayKey(now, ctx.state.settings.timezone);
      if (day !== this.outingsDay) {
        this.outingsDay = day;
        this.outingsToday = 0;
      }
      const out =
        now >= this.holdUntil
          ? pickOuting(ctx.state, now, ctx.quietForMs ?? 0, this.outingsToday, this.random)
          : null;
      if (out) {
        this.outingsToday++;
        this.startVisit(out.kind === "walk_out" ? "park" : null, now, out.durationMs, "outing", false);
        return;
      }
      const pick = pickIdleBehavior(ctx.state, now, this.prevBehavior, this.random);
      this.prevBehavior = pick.behavior;
      const anim = BEHAVIOR_ANIMATION[pick.behavior] ?? "idle";
      this.idle = { behavior: pick.behavior, anim, until: now + pick.durationMs };
      if (pick.behavior === "walk") {
        const tx = WALK_MIN_X + Math.floor(this.random() * (WALK_MAX_X - WALK_MIN_X));
        this.walkTo(tx, WALK_Y, "walk");
        this.idle.until = now; // pick something else on arrival
        return;
      }
    }
    if (this.goToAnchorOf(this.idle.anim)) return;
    this.setAnim(this.idle.anim, now);
  }

  /** Where he is and what he does in a place (the park walk moves both of them). */
  private atPlace(v: Visit, now: number): void {
    if (!v.place) {
      this.hidden = true;
      return;
    }
    this.place = v.place;
    this.hidden = false;
    const spot = PLACE_SPOTS[v.place].me;
    this.setAnim(spot.anim, now);
    if (v.place === "park") {
      const span = PARK_PATH.to - PARK_PATH.from;
      const travelled = ((now - v.since) / 1000) * (ANIMATIONS.stroll?.speed ?? 12);
      const lap = travelled % (span * 2);
      this.flip = lap > span;
      this.x = this.flip ? PARK_PATH.to - (lap - span) : PARK_PATH.from + lap;
    } else {
      this.flip = false;
      this.x = spot.x;
    }
    this.y = spot.y;
  }

  private friendFrame(now: number): FriendFrame | null {
    const v = this.visit;
    if (!v?.place || !v.friend || this.place !== v.place) return null;
    const spot = PLACE_SPOTS[v.place].friend;
    if (!spot) return null;
    const anim = ANIMATIONS[spot.anim] ?? ANIMATIONS.idle!;
    // A little out of step with the companion, so they don't move like twins.
    const { index, frame } = frameAt(anim, now - v.since + 400);
    const park = v.place === "park";
    return {
      seed: v.seed,
      anim: anim.id,
      frameIndex: index,
      pose: frame.pose,
      x: park ? Math.round(this.x + (this.flip ? 16 : -16)) : spot.x,
      y: spot.y,
      flip: park ? this.flip : true,
    };
  }

  frame(ctx: DirectorContext): DirectorFrame {
    const anim = ANIMATIONS[this.anim] ?? ANIMATIONS.idle!;
    const { index, frame } = frameAt(anim, ctx.now - this.animStart);
    const lv = levelForXp(ctx.state.stats.xp.body);
    return {
      anim: anim.id,
      frameIndex: index,
      pose: frame.pose,
      x: Math.round(this.x),
      y: Math.round(this.y),
      flip: this.flip,
      hidden: this.hidden,
      doorOpen: this.doorOpen,
      place: this.place,
      friend: this.friendFrame(ctx.now),
      flash: ctx.now < this.flashUntil,
      stage: visibleBodyStage(
        bodyStage(lv),
        formNow(ctx.state, dayKey(ctx.now, ctx.state.settings.timezone)).body,
      ),
      readingGlasses:
        !ctx.state.look.glasses &&
        levelForXp(ctx.state.stats.xp.mind) >= 3 &&
        (anim.id === "read" || anim.id === "muted"),
    };
  }

  private stateAnimation(ctx: DirectorContext): string | null {
    const s = ctx.state;
    if (modeActive(s, dayKey(ctx.now, s.settings.timezone)) === "sick") return "sick";
    if (ctx.night) return "sleep";
    if (s.mute && ctx.now < s.mute.until) return "muted";
    return null;
  }

  private setAnim(id: string, now: number): void {
    if (this.anim !== id) {
      this.anim = id;
      this.animStart = now;
    }
  }

  private walkTo(x: number, y: number, anim = "walk"): void {
    if (Math.abs(x - this.x) < 1 && Math.abs(y - this.y) < 1) return;
    this.target = { x, y, anim };
  }

  /** Walk to the animation's anchor first. Returns true while walking. */
  private goToAnchorOf(animId: string): boolean {
    const anchorId = ANIMATIONS[animId]?.anchor;
    const anchor = animId === "sleep" ? ANCHORS.bed_lie : anchorId ? ANCHORS[anchorId] : undefined;
    if (!anchor) {
      if (this.y !== WALK_Y && animId !== "talk" && animId !== "listen" && animId !== "think") {
        this.walkTo(this.x, WALK_Y);
        return Boolean(this.target);
      }
      return false;
    }
    if (Math.abs(anchor.x - this.x) >= 1 || Math.abs(anchor.y - this.y) >= 1) {
      this.walkTo(anchor.x, anchor.y);
      return Boolean(this.target);
    }
    return false;
  }

  private stepTowards(dt: number): void {
    const t = this.target!;
    const anim = ANIMATIONS[t.anim] ?? ANIMATIONS.walk!;
    const speed = anim.speed ?? 22;
    const dx = t.x - this.x;
    const dy = t.y - this.y;
    const dist = Math.hypot(dx, dy);
    const step = (speed * dt) / 1000;
    this.setAnim(t.anim, this.lastUpdate);
    if (Math.abs(dx) > 0.5) this.flip = dx < 0;
    if (dist <= step || dist < 0.5) {
      this.x = t.x;
      this.y = t.y;
      this.target = null;
      this.flip = false;
    } else {
      this.x += (dx / dist) * step;
      this.y += (dy / dist) * step;
    }
  }

  private runSequence(ctx: DirectorContext): void {
    const { now } = ctx;
    if (this.seq.length === 0) {
      const next = this.queue.shift()!;
      this.seq = [...(SEQUENCES[next.name] ?? [])];
      this.seqItem = next.item;
      this.stepStarted = false;
      this.activity = null;
      this.idle = null;
    }
    const step = this.seq[0];
    if (!step) return;
    const done = () => {
      this.seq.shift();
      this.stepStarted = false;
    };
    if ("walk" in step) {
      if (!this.stepStarted) {
        this.stepStarted = true;
        const dest =
          step.walk === "item"
            ? { x: this.seqItem ? itemCenterX(this.seqItem) : ANCHORS.center!.x, y: WALK_Y }
            : typeof step.walk === "number"
              ? { x: step.walk, y: WALK_Y }
              : (ANCHORS[step.walk] ?? ANCHORS.center!);
        this.walkTo(dest.x, dest.y, step.anim ?? "walk");
        if (!this.target) done();
        return;
      }
      done();
      return;
    }
    if ("play" in step) {
      if (!this.stepStarted) {
        this.stepStarted = true;
        this.stepUntil = now + step.ms;
        this.setAnim(step.play, now);
        if (step.play === "exit_door")
          this.target = { x: ANCHORS.door!.x, y: ANCHORS.door_out!.y, anim: "exit_door" };
        if (step.play === "enter_door")
          this.target = { x: ANCHORS.door!.x, y: ANCHORS.door!.y, anim: "enter_door" };
        if (step.play === "stage_up" || step.play === "celebrate") this.flip = false;
      }
      if (now >= this.stepUntil) done();
      else this.setAnim(step.play, now);
      return;
    }
    if ("wait" in step) {
      if (!this.stepStarted) {
        this.stepStarted = true;
        this.stepUntil = now + step.wait;
      }
      if (now >= this.stepUntil) done();
      return;
    }
    if ("door" in step) this.doorOpen = step.door === "open";
    else if ("hide" in step) this.hidden = true;
    else if ("show" in step) {
      const a = ANCHORS.door_out!;
      this.x = a.x;
      this.y = a.y;
      this.hidden = false;
    } else if ("place" in step && this.seqItem) this.pendingItems.delete(this.seqItem);
    done();
    if (this.seq.length === 0 && this.seqItem) {
      this.pendingItems.delete(this.seqItem);
      this.seqItem = null;
    }
  }
}
